import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { type OpportunityType, useOpportunitiesList } from '@/api/opportunity';
import { type DashboardWidgetComponentProps } from '@/components/Dashboard/types';
import {
  InstantSearch,
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
  InstantSearchSortOrder,
  type QueryState,
  useInstantSearchState,
} from '@/components/InstantSearch';
import { getSolQueryParamsNewFromQueryState } from '@/components/InstantSearch/util/search-util';
import { Link } from '@/components/typography';
import { USDateViewFormat } from '@/constants/formats';
import { ROUTES } from '@/router';

const DEFAULT_QUERY_PARAMS: QueryState = {
  page: 1,
  size: 20,
  sortBy: 'closeDate',
  sortOrder: InstantSearchSortOrder.DESC,
  search: '',
  filters: [
    {
      field: 'status',
      operator: 'in',
      operand: ['CLOSED_WON'],
    },
  ],
};

const ClosedWonWidget = ({ pageFilters }: DashboardWidgetComponentProps) => {
  return (
    <InstantSearch urlSync={false} defaultQueryState={DEFAULT_QUERY_PARAMS}>
      <ClosedWonTable startDate={pageFilters.startDate} endDate={pageFilters.endDate} />
    </InstantSearch>
  );
};

const ClosedWonTable = ({ startDate, endDate }: { startDate?: string; endDate?: string }) => {
  const { queryState } = useInstantSearchState();
  const { opportunityOverview, isLoading, error } = useOpportunitiesList(
    getSolQueryParamsNewFromQueryState(queryState),
    startDate,
    endDate,
  );

  const columns = useMemo<InstantSearchDataTableColumnDef<OpportunityType>[]>(
    () => [
      {
        header: t`Company`,
        id: 'companyId',
        accessorKey: 'companyId',
        enableSorting: true,
        minSize: 140,
        flex: 1,
        cell: ({ row }) => {
          const record = row.original;
          return record.account.company?.name ?? record.account.name ?? '-';
        },
      },
      {
        header: t`Opportunity`,
        id: 'name',
        accessorKey: 'name',
        enableSorting: true,
        minSize: 160,
        flex: 1,
        cell: ({ row }) => (
          <Link variant="caption1" to={ROUTES.opportunityById(row.original.id)}>
            {row.original.name}
          </Link>
        ),
      },
      {
        header: t`Close Date`,
        id: 'closeDate',
        accessorKey: 'closeDate',
        meta: { align: 'end' },
        enableSorting: true,
        size: 120,
        cell: ({ row }) => dayjs(row.original.closeDate).format(USDateViewFormat),
      },
    ],
    [],
  );

  return (
    <div style={{ padding: 8 }}>
      <InstantSearchDataTable<OpportunityType>
        loading={isLoading}
        error={error}
        columns={columns}
        data={opportunityOverview}
        emptyMessage={<Trans>No Closed Won opportunities found.</Trans>}
        pagination={false}
        getRowId={(record) => record.id}
        hideFilterDetails
        initialColumnPinning={{
          left: ['companyId'],
        }}
      />
    </div>
  );
};

export default ClosedWonWidget;
